import { useState, useMemo, useCallback } from 'react';

export interface UseSearchOptions<T> {
  data: T[];
  searchFields?: (keyof T)[];
  searchFn?: (item: T, query: string) => boolean;
  initialQuery?: string;
}

export interface UseSearchReturn<T> {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filteredData: T[];
  clearSearch: () => void;
}

/**
 * Hook for filtering data based on search query
 * Supports custom search fields or custom search function
 * 
 * @param options - Configuration for search behavior
 * @returns Search state and filtered data
 * 
 * @example
 * // Simple search on specific fields
 * const { searchQuery, setSearchQuery, filteredData } = useSearch({
 *   data: users,
 *   searchFields: ['name', 'email', 'username']
 * });
 * 
 * @example
 * // Custom search function
 * const { searchQuery, setSearchQuery, filteredData } = useSearch({
 *   data: locations,
 *   searchFn: (item, query) => {
 *     return item._id.toLowerCase().includes(query.toLowerCase()) ||
 *            item.name.toLowerCase().includes(query.toLowerCase());
 *   }
 * });
 */
export const useSearch = <T extends Record<string, any>>({
  data,
  searchFields,
  searchFn,
  initialQuery = '',
}: UseSearchOptions<T>): UseSearchReturn<T> => {
  const [searchQuery, setSearchQuery] = useState<string>(initialQuery);

  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) {
      return data;
    }

    const query = searchQuery.toLowerCase().trim();

    if (searchFn) {
      return data.filter(item => searchFn(item, query));
    }

    if (searchFields && searchFields.length > 0) {
      return data.filter(item => {
        return searchFields.some(field => {
          const value = item[field];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(query);
        });
      });
    }

    // Default: search all string/number fields
    return data.filter(item => {
      return Object.values(item).some(value => {
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(query);
      });
    });
  }, [data, searchQuery, searchFields, searchFn]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    filteredData,
    clearSearch,
  };
};
